import { atom } from "jotai";
import { z } from "zod";
import { JotaiTool } from "../../index";

export const SetNodeLoadingParams = z.object({
  nodeId: z.string().describe("The ID of the node to set loading state for"),
  isLoading: z
    .boolean()
    .describe(
      "Whether the node should show loading state (true) or not (false)",
    ),
});

export type SetNodeLoadingParamsType = z.infer<typeof SetNodeLoadingParams>;

// Atom to track which single node is currently loading (if any)
export const copilotLoadingNodeIdAtom = atom<string | null>(null);
copilotLoadingNodeIdAtom.debugLabel = "copilotLoadingNodeIdAtom";

/**
 * Atom to track the current tool message
 * This captures displayMessage values from any running tool
 */
export const currentToolMessageAtom = atom<{
  displayMessage: string;
  timestamp: number;
} | null>(null);
currentToolMessageAtom.debugLabel = "currentToolMessageAtom";
/**
 * Setter atom to set a tool message with cleanup support
 */
export const setCurrentToolMessageAtom = atom(
  null,
  (_, set, action: { displayMessage?: string } | { clear: true }) => {
    if ("clear" in action) {
      set(currentToolMessageAtom, null);
    } else if (action.displayMessage) {
      set(currentToolMessageAtom, {
        displayMessage: action.displayMessage,
        timestamp: Date.now(),
      });
    }
  },
);
setCurrentToolMessageAtom.debugLabel = "setCurrentToolMessageAtom";

const setNodeLoadingExecuteAtom = atom(
  null,
  async (get, set, { args }: { args: SetNodeLoadingParamsType }) => {
    try {
      const params = SetNodeLoadingParams.parse(args);
      const { nodeId, isLoading } = params;

      // Set the loading state for the specified node
      // Only one node can be loading at a time
      if (isLoading) {
        set(copilotLoadingNodeIdAtom, nodeId);
      } else {
        // Clear loading state if this node was the one loading (atomic operation)
        set(copilotLoadingNodeIdAtom, (current) => {
          const wasLoading = current === nodeId;
          if (wasLoading) {
            // Clear the tool message when loading ends
            set(setCurrentToolMessageAtom, { clear: true });
            return null;
          }
          return current;
        });
      }

      return {
        content: [
          {
            type: "text",
            text: `Successfully set loading state for node ${nodeId} to ${isLoading}`,
          },
        ],
      };
    } catch (error) {
      return {
        $error: {
          message: `Error setting node loading state: ${error instanceof Error ? error.message : String(error)}`,
        },
      };
    }
  },
);

setNodeLoadingExecuteAtom.debugLabel = "setNodeLoadingExecuteAtom";

export const setNodeLoadingAtom = atom<JotaiTool<SetNodeLoadingParamsType>>(
  () => ({
    name: "set_node_loading",
    parameters: SetNodeLoadingParams,
    execute: setNodeLoadingExecuteAtom,
    description: setNodeLoadingDescription,
  }),
);

setNodeLoadingAtom.debugLabel = "setNodeLoadingAtom";

export const setNodeLoadingDescription = `Sets the loading state for a specific node on the canvas. Only one node can be in loading state at a time.

PURPOSE: Shows or hides a loading indicator on a node when it's being processed by tools or operations.

WHEN TO CALL:
- Before starting any long-running operation on a node (set isLoading: true)
- After completing any operation on a node (set isLoading: false)
- When a tool is about to modify, analyze, or interact with a specific node
- To provide visual feedback that a node is being worked on

WHAT IT DOES:
- Sets a loading state for the specified node ID (only one node can be loading at a time)
- Setting isLoading=true on a new node will automatically clear loading from any previously loading node
- The UI can show a spinner, overlay, or other loading indicator
- Helps users understand which node is currently being processed

PARAMETERS:
- nodeId: The ID of the node to set loading state for
- isLoading: true to show loading, false to hide loading

USAGE PATTERNS:
1. Set loading before operation: set_node_loading(nodeId="node-123", isLoading=true)
2. Clear loading after operation: set_node_loading(nodeId="node-123", isLoading=false)

IMPORTANT: 
- Only one node can be loading at a time
- Always clear the loading state (isLoading=false) when your operation completes, whether it succeeds or fails
- Setting loading=true on a different node will automatically clear the previous loading node`;
